import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Brand } from "@/components/Brand";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/app", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Signed in");
      navigate(location.state?.from || "/app");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF7EF]">
      <div className="hidden lg:flex flex-col justify-between bg-neutral-950 text-white p-12 bg-grid-dark relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{background: "radial-gradient(600px 400px at 30% 20%, rgba(201,169,97,0.25), transparent 60%)"}}></div>
        <div className="relative">
          <Brand size="md" variant="dark" asLink />
        </div>
        <div className="relative">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#D4B87D] mb-4 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-[#C9A961]"></span>
            Concierge access
          </div>
          <h1 className="font-serif font-black text-5xl xl:text-6xl tracking-[-0.02em] leading-[0.95]">
            Welcome to your<br />
            <span className="italic text-gold-gradient">payroll partner.</span>
          </h1>
          <p className="mt-6 text-neutral-400 max-w-md leading-relaxed">
            Every client, every payslip, every attendance record — in one impeccably organised workspace, run by your servicing partner.
          </p>
        </div>
        <div className="relative font-mono text-xs text-neutral-500 flex justify-between">
          <span>© 2026 HG Consultancy · Harguaon Group</span>
          <span className="text-[#D4B87D]">v 1.0</span>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-16 py-12">
        <div className="max-w-sm w-full mx-auto">
          <div className="lg:hidden mb-8"><Brand size="md" variant="light" asLink /></div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E] mb-3">Access</div>
          <h2 className="font-serif font-black text-4xl tracking-[-0.02em] mb-2">Welcome back.</h2>
          <p className="text-sm text-neutral-600 mb-10">Sign in with the credentials issued to your role.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                autoComplete="email"
                className="w-full px-3 py-3 bg-white border border-[#E8DFC5] focus:border-[#C9A961] focus:ring-1 focus:ring-[#C9A961] outline-none transition-colors font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                autoComplete="current-password"
                className="w-full px-3 py-3 bg-white border border-[#E8DFC5] focus:border-[#C9A961] focus:ring-1 focus:ring-[#C9A961] outline-none transition-colors font-mono text-sm"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="border border-[#B33A1F] bg-[#B33A1F]/5 px-3 py-2 text-sm text-[#B33A1F] font-mono">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full flex items-center justify-between px-4 py-3.5 bg-neutral-950 text-[#D4B87D] font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#E8DFC5] text-sm text-neutral-600">
            Need a workspace?{" "}
            <Link to="/register" className="text-[#8C6F1E] hover:underline" data-testid="link-to-register">
              Register your firm →
            </Link>
          </div>

          <div className="mt-6 text-xs font-mono text-neutral-500 leading-relaxed">
            Super admin (demo): kajalpandey@gmail.com / Admin@2026
          </div>
        </div>
      </div>
    </div>
  );
}
